import { Actions, createEffect, ofType } from '@ngrx/effects'
import { SnackbarService } from '@shared/utils/snackbar.service'
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { Store } from '@ngrx/store'
import { ChangeRequestService } from '../../service/change-request.service'
import { ChangeRequestActions } from '../actions/change-request.actions'
import { ChangeRequestSelectors } from '../selectors/change-request.selectors'
import { ChangeType } from '../../model'

@Injectable()
export class ChangeRequestChangesEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private changeRequestService: ChangeRequestService,
    private snackbarService: SnackbarService
  ) {}

  applyChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.applyChanges),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ChangeRequestSelectors.selectCurrentChangeRequest),
        this.store$.select(ChangeRequestSelectors.selectChangeItemEntities)
      ),
      filter(([_, currentScope, currentChangeRequest]) => !!(currentScope && currentChangeRequest)),
      map(([{ payload }, currentScope, currentChangeRequest, changeItemEntities]) => {
        const scopeId = currentScope.identity.id
        const changeRequestId = currentChangeRequest.id
        const changeItem = changeItemEntities[payload.identity]
        const changeItemId = changeItem?.uuid

        if (changeItem) {
          if (changeItem.changeType === ChangeType.ADDED) {
            const payloadChanges = Object.keys({ ...payload.data }).reduce(
              (acc, key) => ({ ...acc, [key]: payload.data[key]?.new }),
              {}
            )
            const updatedData = { ...(changeItem.data?.data || changeItem.data), ...payloadChanges }

            payload = {
              ...changeItem,
              data: { data: updatedData },
            }
          } else {
            payload = {
              ...changeItem,
              data: { ...changeItem.data, ...payload.data },
            }
          }

          return ChangeRequestActions.modifyChanges({ scopeId, changeRequestId, changeItemId, payload })
        }

        return ChangeRequestActions.createChanges({ scopeId, changeRequestId, payload })
      })
    )
  )

  createChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.createChanges),
      switchMap(({ scopeId, changeRequestId, payload }) =>
        this.changeRequestService.applyChanges(scopeId, changeRequestId, payload).pipe(
          map((changeItem: any) => ChangeRequestActions.applyChangesSuccess({ changeItem })),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar('An error occurred while applying changes')
            return of(ChangeRequestActions.applyChangesFail({ error: error as Error }))
          })
        )
      )
    )
  )

  modifyChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.modifyChanges),
      switchMap(({ scopeId, changeRequestId, changeItemId, payload }) =>
        this.changeRequestService.modifyChange(scopeId, changeRequestId, changeItemId, payload).pipe(
          map((changeItem: any) => ChangeRequestActions.applyChangesSuccess({ changeItem })),
          catchError((error: unknown) => {
            this.snackbarService.showErrorSnackbar('An error occurred while applying changes')
            return of(ChangeRequestActions.applyChangesFail({ error: error as Error }))
          })
        )
      )
    )
  )

  removeChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.removeChanges),
      filter(({ changeItemUuid }) => !!changeItemUuid),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ChangeRequestSelectors.selectCurrentChangeRequest)
      ),
      switchMap(([{ changeItemUuid }, currentScope, currentChangeRequest]) =>
        this.changeRequestService
          .deleteChangeItem(currentScope.identity.id, currentChangeRequest.id, changeItemUuid)
          .pipe(
            map(() => ChangeRequestActions.removeChangesSuccess({ changeItemUuid })),
            catchError((error: unknown) => {
              this.snackbarService.showErrorSnackbar('An error occurred while removing change item')
              console.error(JSON.stringify(error))
              return of(ChangeRequestActions.removeChangesFail({ error: error as Error }))
            })
          )
      )
    )
  )
}
